import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import Nameplate from "@/components/Nameplate";
import { FlagBadge } from "@/lib/flags";

/**
 * GuildModal — browse, create, join, leave, manage guilds.
 * V2.5.2 — full Member List with Leader/Officer markers + Approve/Decline flow.
 */
export default function GuildModal({ onClose, tier }) {
  const [tab, setTab] = useState("browse"); // browse | mine | leaderboard | create
  const [list, setList] = useState([]);
  const [mine, setMine] = useState(null);     // hydrated guild (with members_detail, etc.)
  const [board, setBoard] = useState([]);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [form, setForm] = useState({ name: "", description: "" });

  useEffect(() => { reload(); }, []);
  async function reload() {
    try {
      const [b, m, l] = await Promise.all([
        api.get("/guilds"),
        api.get("/guilds/mine"),
        api.get("/guilds/leaderboard/all"),
      ]);
      setList(b.data);
      setBoard(l.data);
      if (m.data?.id) {
        // hydrate full /guilds/{id} so we get members_detail + pending
        const { data: full } = await api.get(`/guilds/${m.data.id}`);
        setMine(full);
      } else {
        setMine(null);
      }
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  function flash(t) { setMsg(t); setTimeout(() => setMsg(""), 2000); }
  function fail(e)  { setErr(fmtErr(e.response?.data?.detail) || e.message); setTimeout(() => setErr(""), 4000); }

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/guilds", form);
      setForm({ name: "", description: "" });
      flash("Guild created!"); setTab("mine"); await reload();
    } catch (e2) { fail(e2); }
  }
  async function requestJoin(id) {
    try {
      const { data } = await api.post("/guilds/join", { guild_id: id });
      flash(data.already_member ? "Already a member." : "Request sent — awaiting approval.");
      await reload();
    } catch (e) { fail(e); }
  }
  async function leave() {
    if (!window.confirm("Leave this guild?")) return;
    try { await api.post("/guilds/leave", { guild_id: mine.id }); await reload(); setTab("browse"); }
    catch (e) { fail(e); }
  }
  async function dissolve() {
    if (!window.confirm("Permanently dissolve this guild? Members will lose access.")) return;
    try { await api.post(`/guilds/${mine.id}/dissolve`); await reload(); setTab("browse"); }
    catch (e) { fail(e); }
  }
  async function approve(uid) {
    try { await api.post("/guilds/approve", { guild_id: mine.id, user_id: uid }); flash("Approved."); await reload(); }
    catch (e) { fail(e); }
  }
  async function decline(uid) {
    try { await api.post("/guilds/decline", { guild_id: mine.id, user_id: uid }); flash("Declined."); await reload(); }
    catch (e) { fail(e); }
  }
  async function kick(uid, name) {
    if (!window.confirm(`Kick ${name} from the guild?`)) return;
    try { await api.post("/guilds/kick", { guild_id: mine.id, user_id: uid }); flash("Member removed."); await reload(); }
    catch (e) { fail(e); }
  }
  async function appoint(uid) {
    try { await api.post("/guilds/officer/appoint", { guild_id: mine.id, user_id: uid }); flash("Officer appointed."); await reload(); }
    catch (e) { fail(e); }
  }
  async function demote(uid) {
    try { await api.post("/guilds/officer/demote", { guild_id: mine.id, user_id: uid }); flash("Officer demoted."); await reload(); }
    catch (e) { fail(e); }
  }

  const canCreate = tier === "pro" || tier === "elite";
  const viewerRole = mine?.viewer_role || "none";
  const canApprove = viewerRole === "leader" || viewerRole === "officer";
  const pendingCount = (mine?.pending_detail || []).length;

  return (
    <div data-testid="guild-modal" className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4 font-mono" onClick={onClose}>
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl max-w-3xl w-full p-5 shadow-2xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-3 border-b border-slate-800 pb-2">
          <h2 className="text-base font-bold text-white"><i className="fa-solid fa-shield-halved text-indigo-300 mr-2"/>Guilds</h2>
          <button onClick={onClose} data-testid="guild-close" className="text-slate-500 hover:text-rose-300"><i className="fa-solid fa-xmark"/></button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 text-[10px] uppercase font-bold mb-3 flex-wrap">
          {[["browse","Browse"],["mine","My Guild"],["leaderboard","Leaderboard"],["create","Create"]].map(([k,l]) => (
            <button key={k} data-testid={`guild-tab-${k}`} onClick={() => setTab(k)}
              className={`relative px-3 py-1.5 rounded ${tab === k ? "bg-indigo-500/20 text-indigo-200 border border-indigo-500/40" : "text-slate-500 hover:text-slate-300"}`}>
              {l}
              {k === "mine" && pendingCount > 0 && canApprove && (
                <span data-testid="pending-count-badge" className="absolute -top-1 -right-1 bg-amber-400 text-slate-900 text-[9px] font-black rounded-full w-4 h-4 flex items-center justify-center">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {msg && <div className="mb-2 text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-1 rounded">{msg}</div>}
        {err && <div className="mb-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 px-2 py-1 rounded">{err}</div>}

        {tab === "browse" && (
          <div className="space-y-2">
            {list.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No guilds yet. Be the first to create one.</div>}
            {list.map(g => (
              <div key={g.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-white truncate">{g.name}</div>
                  <div className="text-[10px] text-slate-400 truncate">{g.description || "—"}</div>
                  <div className="text-[10px] text-slate-500">{g.member_count}/30 members · {g.status}</div>
                </div>
                <button data-testid={`guild-join-${g.id}`} disabled={g.is_member || g.is_full || g.status !== "active"}
                  onClick={() => requestJoin(g.id)}
                  className={`px-3 py-1.5 text-[10px] font-bold rounded ${g.is_member ? "bg-emerald-600/30 text-emerald-200" : g.is_full || g.status !== "active" ? "bg-slate-800 text-slate-600" : "bg-indigo-600 hover:bg-indigo-700 text-white"}`}>
                  {g.is_member ? "Member" : g.is_full ? "Full" : g.status !== "active" ? g.status : "Request to Join"}
                </button>
              </div>
            ))}
          </div>
        )}

        {tab === "mine" && (
          <div>
            {mine ? (
              <div className="space-y-4">
                {/* Header */}
                <div className="bg-slate-900/40 border border-indigo-500/30 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-lg font-bold text-white">{mine.name}</div>
                      <div className="text-xs text-slate-400">{mine.description}</div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {(mine.members || []).length}/30 members · status: {mine.status} · you are: <span className="text-indigo-300 font-bold uppercase">{viewerRole}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      {viewerRole === "leader" && (
                        <button onClick={dissolve} data-testid="guild-dissolve" className="text-[10px] text-rose-300 hover:text-rose-200 px-2 py-1 border border-rose-500/30 rounded">Dissolve</button>
                      )}
                      {viewerRole !== "leader" && viewerRole !== "none" && (
                        <button onClick={leave} data-testid="guild-leave" className="text-[10px] text-slate-400 hover:text-slate-200 px-2 py-1 border border-slate-700 rounded">Leave</button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pending Requests (Leader / Officer only) */}
                {canApprove && pendingCount > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/30 rounded-lg p-3" data-testid="pending-requests-panel">
                    <div className="text-[11px] uppercase font-bold text-amber-300 mb-2 tracking-wider"><i className="fa-solid fa-user-clock mr-1"/>Pending Requests ({pendingCount})</div>
                    <div className="space-y-2">
                      {(mine.pending_detail || []).map(p => (
                        <div key={p.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded p-2" data-testid={`pending-${p.id}`}>
                          <Link to={`/u/${p.id}`} onClick={onClose}>
                            <Nameplate userId={p.id} name={p.name} country={p.country} avatar={p.avatar} size="sm"/>
                          </Link>
                          <div className="flex gap-1">
                            <button onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}
                              className="px-2 py-1 bg-emerald-600/30 hover:bg-emerald-600/50 border border-emerald-500/50 text-emerald-200 rounded text-[10px] font-bold">
                              <i className="fa-solid fa-check mr-1"/>Approve
                            </button>
                            <button onClick={() => decline(p.id)} data-testid={`decline-${p.id}`}
                              className="px-2 py-1 bg-rose-600/30 hover:bg-rose-600/50 border border-rose-500/50 text-rose-200 rounded text-[10px] font-bold">
                              <i className="fa-solid fa-xmark mr-1"/>Decline
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Member List */}
                <div data-testid="guild-members-list">
                  <div className="text-[11px] uppercase font-bold text-slate-400 mb-2 tracking-wider"><i className="fa-solid fa-users mr-1"/>Members</div>
                  <div className="space-y-1.5">
                    {(mine.members_detail || []).map(m => {
                      const role = m.guild_role;
                      const isLeader  = role === "leader";
                      const isOfficer = role === "officer";
                      // Permission matrix (mirrors backend):
                      const canKick = (viewerRole === "leader" && !isLeader)
                                   || (viewerRole === "officer" && !isLeader && !isOfficer);
                      const canPromote = viewerRole === "leader" && !isLeader && !isOfficer
                                       && (mine.officers || []).length < 2;
                      const canDemote  = viewerRole === "leader" && isOfficer;
                      const roleBadge = isLeader
                        ? { text: "Guild Leader", color: "bg-amber-500/20 text-amber-300 border-amber-500/40" }
                        : isOfficer
                        ? { text: "Officer", color: "bg-indigo-500/20 text-indigo-300 border-indigo-500/40" }
                        : { text: "Member", color: "bg-slate-800 text-slate-400 border-slate-700" };
                      return (
                        <div key={m.id} data-testid={`member-row-${m.id}`}
                          className={`flex items-center justify-between bg-slate-900/40 border rounded-lg px-3 py-2 ${isLeader ? "border-amber-500/30" : isOfficer ? "border-indigo-500/30" : "border-slate-800"}`}>
                          <Link to={`/u/${m.id}`} onClick={onClose} className="flex-1 min-w-0 flex items-center gap-3 hover:opacity-90">
                            <Nameplate userId={m.id} name={m.name} country={m.country} avatar={m.avatar} nameColor={m.name_color} size="md"/>
                          </Link>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded border ${roleBadge.color}`}>{roleBadge.text}</span>
                            {canPromote && (
                              <button onClick={() => appoint(m.id)} data-testid={`appoint-${m.id}`}
                                className="text-[9px] px-2 py-1 bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-500/50 text-indigo-200 rounded font-bold">
                                <i className="fa-solid fa-star mr-1"/>Make Officer
                              </button>
                            )}
                            {canDemote && (
                              <button onClick={() => demote(m.id)} data-testid={`demote-${m.id}`}
                                className="text-[9px] px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded font-bold">
                                Step Down
                              </button>
                            )}
                            {canKick && (
                              <button onClick={() => kick(m.id, m.name)} data-testid={`kick-${m.id}`}
                                className="text-[9px] px-2 py-1 bg-rose-600/30 hover:bg-rose-600/50 border border-rose-500/50 text-rose-200 rounded font-bold">
                                <i className="fa-solid fa-user-xmark mr-1"/>Kick
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {viewerRole === "leader" && (
                    <div className="text-[10px] text-slate-600 mt-3 italic">
                      You can appoint up to <span className="text-indigo-300 font-bold">2 Officers</span>. Officers can remove non-officer members. Only you can kick officers or dissolve the guild.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-xs text-slate-500 text-center py-6">You're not in a guild yet. Browse or create one.</div>
            )}
          </div>
        )}

        {tab === "leaderboard" && (
          <div className="space-y-1">
            {board.length === 0 && <div className="text-xs text-slate-500 text-center py-6">No ranked guilds yet.</div>}
            {board.map((g, i) => (
              <div key={g.id} className="flex items-center justify-between bg-slate-900/40 border border-slate-800 rounded px-3 py-2 text-xs">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-7 text-center font-black ${i === 0 ? "text-amber-300" : i === 1 ? "text-slate-300" : i === 2 ? "text-orange-400" : "text-slate-500"}`}>#{i+1}</div>
                  <div className="min-w-0">
                    <div className="text-white font-bold truncate">{g.name}</div>
                    <div className="text-[9px] text-slate-500">{g.member_count} members</div>
                  </div>
                </div>
                <div className={`font-bold tabular-nums ml-2 ${g.total_pnl >= 0 ? "text-emerald-300" : "text-rose-300"}`}>${g.total_pnl.toFixed(2)}</div>
              </div>
            ))}
            <div className="text-[10px] text-slate-600 italic pt-2">
              Tip: the in-guild member roster on <span className="text-amber-300">My Guild</span> shows each player's national flag (replacing the legacy Points column).
            </div>
          </div>
        )}

        {tab === "create" && (
          <form onSubmit={create} className="space-y-3 max-w-md" data-testid="guild-create-form">
            {!canCreate && <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">Pro or Elite tier required ($5/mo). Framework-mode is live but billing will activate after Stripe live key is whitelisted.</div>}
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Name</div>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} maxLength={40} required
                data-testid="guild-name-input" className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded text-sm"/>
            </div>
            <div>
              <div className="text-[10px] uppercase font-bold text-slate-500 mb-1">Description (optional)</div>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} maxLength={300}
                data-testid="guild-desc-input" className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded text-sm resize-none"/>
            </div>
            <button type="submit" disabled={!canCreate} data-testid="guild-create-submit"
              className={`w-full py-2 rounded text-xs font-bold ${canCreate ? "bg-indigo-600 hover:bg-indigo-700 text-white" : "bg-slate-800 text-slate-500"}`}>
              Create Guild · $5/mo
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// Keep FlagBadge import alive (used in Nameplate via lib) — appease lint
void FlagBadge;
